import materialService from 'ApiService/Student/materialService';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BallTriangle } from 'react-loader-spinner';
import { FiEye, FiFile, FiVideo, FiFileText } from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';
import VideoPlayer from 'pages/Student/VideoLinks/components/VideoPlayer';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const AllTrainingMaterials = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const [searchParams] = useSearchParams();
  const highlightedMaterialId =
    searchParams.get('releaseId') || searchParams.get('materialId');

  const fetchAllMaterials = async () => {
    try {
      setLoading(true);
      const response = await materialService.getAllMaterials();
      if (response.data.success) {
        setMaterials(response.data.materials);
        toast.success(response.data.message);
      } else {
        toast.error('No training materials found.');
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch training materials.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllMaterials();
  }, []);

  const normalizeAssetUrl = (value) => {
    if (!value || typeof value !== 'string') {
      return '';
    }

    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return '';
    }

    if (
      /^(https?:)?\/\//i.test(trimmedValue) ||
      trimmedValue.startsWith('blob:') ||
      trimmedValue.startsWith('data:')
    ) {
      return trimmedValue;
    }

    if (!API_BASE_URL) {
      return trimmedValue;
    }

    return `${API_BASE_URL.replace(/\/+$/, '')}/${trimmedValue.replace(
      /^\/+/,
      ''
    )}`;
  };

  const resolveMaterialContent = (material, typeOverride = null) => {
    const materialType = (
      typeOverride ||
      material.type ||
      material.materialType ||
      'content'
    ).toLowerCase();
    const rawContent =
      material.content || material.Url || material.fileUrl || '';

    if (!rawContent || materialType === 'content') {
      return rawContent;
    }

    return normalizeAssetUrl(rawContent);
  };

  useEffect(() => {
    if (!highlightedMaterialId || materials.length === 0) {
      return;
    }

    const targetElement = document.getElementById(
      `material-card-${highlightedMaterialId}`
    );

    if (!targetElement) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 150);

    return () => window.clearTimeout(timeoutId);
  }, [highlightedMaterialId, materials]);

  const openMaterial = (material, typeOverride = null, itemKey = null) => {
    const materialType =
      typeOverride || material.type || material.materialType || 'content';
    const normalizedType = materialType.toLowerCase();

    if (normalizedType === 'video') {
      const nextVideo = {
        key: itemKey,
        title: material.title || material.subtopic?.title || 'Video',
        description: material.description || '',
        content: resolveMaterialContent(material, materialType),
      };

      setSelectedMaterial(null);
      setActiveVideo((currentVideo) =>
        currentVideo?.key === itemKey ? null : nextVideo
      );
      return;
    }

    const materialData = {
      title: material.title || material.subtopic?.title || 'Material',
      description: material.description || '',
      content: resolveMaterialContent(material, materialType),
      type: materialType,
    };
    setActiveVideo(null);
    setSelectedMaterial(materialData);
  };

  const closeModal = () => {
    setSelectedMaterial(null);
  };

  const getTypeFromMaterial = (material) => {
    const type = material.type || material.materialType;
    if (type === 'VIDEO' || type === 'video') return 'video';
    if (type === 'PDF' || type === 'pdf') return 'pdf';
    if (type === 'IMAGE' || type === 'image') return 'image';
    if (type === 'content') return 'content';
    return 'content';
  };

  const hasContent = (material) => {
    return !!(material.content || material.Url || material.fileUrl);
  };

  const getMaterialCardClassName = (materialId, baseClassName) => {
    const isHighlighted = highlightedMaterialId === String(materialId);

    if (!isHighlighted) {
      return baseClassName;
    }

    return `${baseClassName} ring-2 ring-brand-500 ring-offset-2 ring-offset-white dark:ring-offset-navy-900`;
  };

  const renderAssignedBadge = (materialId) => {
    if (highlightedMaterialId !== String(materialId)) {
      return null;
    }

    return (
      <div className="dark:bg-brand-400/10 mb-3 inline-flex rounded-full bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 dark:text-brand-200">
        Assigned from attendance
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <BallTriangle color="#5431ed" />
      </div>
    );
  }

  if (materials.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-gray-500">
        No training materials found.
      </div>
    );
  }

  const selectedMaterialType = (
    selectedMaterial?.type ||
    selectedMaterial?.materialType ||
    ''
  ).toLowerCase();

  const renderInlineVideo = (videoKey) => {
    if (activeVideo?.key !== videoKey || !activeVideo?.content) {
      return null;
    }

    return (
      <div className="mt-4 overflow-hidden rounded-xl border border-blue-100 bg-white p-4 shadow-sm dark:border-navy-700 dark:bg-navy-900">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h5 className="text-sm font-semibold text-navy-700 dark:text-white">
              {activeVideo.title}
            </h5>
            {activeVideo.description && (
              <p className="text-xs text-gray-600 dark:text-gray-300">
                {activeVideo.description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setActiveVideo(null)}
            className="rounded-lg border border-gray-200 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5"
          >
            Hide Video
          </button>
        </div>
        <VideoPlayer url={activeVideo.content} title={activeVideo.title} />
      </div>
    );
  };

  return (
    <div className="mt-20 space-y-4 md:mt-10">
      {materials.map((material, materialIndex) => {
        if (material.materialType === 'video') {
          const videoContent = resolveMaterialContent(material, 'video');
          return (
            <div
              key={material._id || materialIndex}
              id={`material-card-${material._id}`}
              className={getMaterialCardClassName(
                material._id,
                'overflow-hidden rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-shadow dark:bg-navy-800'
              )}
            >
              {renderAssignedBadge(material._id)}
              <div className="mb-2 flex items-center gap-2">
                <span className="rounded bg-purple-100 px-2 py-1 text-xs font-medium text-purple-800">
                  Video Link
                </span>
              </div>
              <div className="flex justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
                    {material.title}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {material.description}
                  </p>
                  {videoContent && (
                    <button
                      type="button"
                      onClick={() =>
                        openMaterial(
                          { ...material, type: 'VIDEO', content: videoContent },
                          'VIDEO',
                          `top-video-${material._id || materialIndex}`
                        )
                      }
                      className="mt-2 inline-flex cursor-pointer items-center gap-1 rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
                    >
                      <FiEye className="h-4 w-4" /> View Video
                    </button>
                  )}
                </div>
                <span className="text-xs text-gray-500">
                  {new Date(material.createdAt).toLocaleDateString()}
                </span>
              </div>
              <div className="mt-2 flex items-center text-xs text-gray-500">
                <span>Teacher: {material.teacher?.name}</span>
              </div>
              {renderInlineVideo(`top-video-${material._id || materialIndex}`)}
            </div>
          );
        }

        if (material.days && material.days.length > 0) {
          return (
            <div
              key={material._id || materialIndex}
              id={`material-card-${material._id}`}
              className={getMaterialCardClassName(
                material._id,
                'border-transparent space-y-3 rounded-xl border p-1 transition-shadow'
              )}
            >
              {renderAssignedBadge(material._id)}
              {material.days.map((day, dayIndex) => (
                <div
                  key={dayIndex}
                  className="rounded-xl border bg-white p-5 shadow-sm dark:bg-navy-800"
                >
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-blue-600">
                        Day {day.lectureDay}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300">
                        Topic: {day.topic}
                      </p>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(material.createdAt).toLocaleDateString()}
                    </span>
                  </div>

                  {day.subtopics && day.subtopics.length > 0 && (
                    <div className="space-y-4">
                      {day.subtopics.map((subtopic, subIndex) => {
                        const type = getTypeFromMaterial(subtopic);
                        const content = resolveMaterialContent(subtopic, type);
                        const hasMaterials = hasContent(subtopic);

                        return (
                          <div
                            key={subIndex}
                            className="rounded-lg border bg-gray-50 p-4 dark:bg-navy-900"
                          >
                            <h4 className="mb-2 font-semibold text-navy-700 dark:text-white">
                              {subtopic.title}
                            </h4>

                            {hasMaterials && (
                              <button
                                type="button"
                                onClick={() =>
                                  openMaterial(
                                    {
                                      ...subtopic,
                                      title: subtopic.title,
                                      type: type,
                                      content: content,
                                    },
                                    type,
                                    `subtopic-video-${
                                      material._id || materialIndex
                                    }-${dayIndex}-${subIndex}`
                                  )
                                }
                                className="inline-flex cursor-pointer items-center gap-1 rounded px-2 py-1 text-sm font-medium text-blue-600 hover:bg-blue-50"
                              >
                                <FiEye className="h-4 w-4" />
                                {type === 'video' && 'Watch Video'}
                                {type === 'pdf' && 'View PDF'}
                                {type === 'image' && 'View Image'}
                                {type === 'content' && 'View Content'}
                              </button>
                            )}

                            {subtopic.materials &&
                              subtopic.materials.length > 0 && (
                                <div className="mt-3 space-y-2">
                                  {subtopic.materials.map((mat, matIndex) => {
                                    const matType = getTypeFromMaterial(mat);
                                    const matContent = resolveMaterialContent(
                                      mat,
                                      matType
                                    );
                                    if (!matContent) return null;

                                    return (
                                      <button
                                        key={matIndex}
                                        type="button"
                                        onClick={() =>
                                          openMaterial(
                                            {
                                              ...mat,
                                              type: matType,
                                              content: matContent,
                                            },
                                            matType,
                                            `material-video-${
                                              material._id || materialIndex
                                            }-${dayIndex}-${subIndex}-${matIndex}`
                                          )
                                        }
                                        className="inline-flex cursor-pointer items-center gap-1 rounded px-2 py-1 text-sm font-medium text-blue-600 hover:bg-blue-50"
                                      >
                                        {matType === 'video' && (
                                          <>
                                            <FiVideo className="h-4 w-4" />{' '}
                                            Watch Video
                                          </>
                                        )}
                                        {matType === 'pdf' && (
                                          <>
                                            <FiFile className="h-4 w-4" /> View
                                            PDF
                                          </>
                                        )}
                                        {matType === 'image' && (
                                          <>
                                            <FiFile className="h-4 w-4" /> View
                                            Image
                                          </>
                                        )}
                                        {matType === 'content' && (
                                          <>
                                            <FiFileText className="h-4 w-4" />{' '}
                                            View Content
                                          </>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            {renderInlineVideo(
                              `subtopic-video-${
                                material._id || materialIndex
                              }-${dayIndex}-${subIndex}`
                            )}
                            {subtopic.materials?.map((mat, matIndex) => (
                              <div
                                key={`inline-video-${
                                  material._id || materialIndex
                                }-${dayIndex}-${subIndex}-${matIndex}`}
                              >
                                {renderInlineVideo(
                                  `material-video-${
                                    material._id || materialIndex
                                  }-${dayIndex}-${subIndex}-${matIndex}`
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="mt-3 flex items-center text-xs text-gray-500">
                    <span>Teacher: {material.teacher?.name}</span>
                  </div>
                </div>
              ))}
            </div>
          );
        }

        const plainContent = resolveMaterialContent(material, 'content');
        return (
          <div
            key={material._id || materialIndex}
            id={`material-card-${material._id}`}
            className={getMaterialCardClassName(
              material._id,
              'overflow-hidden rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-shadow dark:bg-navy-800'
            )}
          >
            {renderAssignedBadge(material._id)}
            <div className="mb-2 flex items-center gap-2">
              <span className="rounded bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                Training Material
              </span>
            </div>
            <div className="flex justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
                  {material.title}
                </h2>
                <p className="text-sm text-gray-600">{material.description}</p>
                {plainContent && (
                  <button
                    type="button"
                    onClick={() =>
                      openMaterial(
                        { ...material, type: 'content', content: plainContent },
                        'content'
                      )
                    }
                    className="mt-2 inline-flex cursor-pointer items-center gap-1 rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
                  >
                    <FiEye className="h-4 w-4" /> View Material
                  </button>
                )}
              </div>
              <span className="text-xs text-gray-500">
                {new Date(material.createdAt).toLocaleDateString()}
              </span>
            </div>
            <div className="mt-2 flex items-center text-xs text-gray-500">
              <span>Teacher: {material.teacher?.name}</span>
            </div>
          </div>
        );
      })}

      {selectedMaterial && (
        <div
          className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="max-h-[90vh] w-full max-w-4xl overflow-auto rounded-lg bg-white p-6 dark:bg-navy-800">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800 dark:text-white">
                {selectedMaterial.title || 'Material'}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="text-2xl text-gray-500 hover:text-gray-700 dark:text-gray-300"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              {selectedMaterial.description && (
                <p className="text-gray-600 dark:text-gray-300">
                  {selectedMaterial.description}
                </p>
              )}

              {selectedMaterialType === 'content' &&
                selectedMaterial.content && (
                  <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-900">
                    <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
                      {selectedMaterial.content}
                    </pre>
                  </div>
                )}

              {selectedMaterialType === 'pdf' && selectedMaterial.content && (
                <div className="text-center">
                  <a
                    href={selectedMaterial.content}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                  >
                    <FiFile className="h-4 w-4" /> Open PDF
                  </a>
                </div>
              )}

              {selectedMaterialType === 'image' && selectedMaterial.content && (
                <div className="text-center">
                  <a
                    href={selectedMaterial.content}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                  >
                    <FiFile className="h-4 w-4" /> Open Image
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllTrainingMaterials;
